import { InternalGameScreenshot } from '../../../api/screenshot-links'
import { ContentLanguageTranslation } from '../../../types/InternalAnalystComment'
import { AnalysisCommentFormValues, isSameTranslation, storedFieldLabelKeys } from '../../review/helpers/reviewHelpers'
import { InternalScreenshotComment, MultipleScreenshotCommentMutation, ScreenshotCommentMutation } from '../types/ImplementationExamples'

export type ScreenshotCommentFormValues = AnalysisCommentFormValues

export const toScreenshotCommentFormValues = (comment?: InternalScreenshotComment | null): ScreenshotCommentFormValues => ({
  analystId: comment?.analystId ?? '',
  published: !!comment?.published,
  comment: comment?.content?.comment ?? null,
})

// Legacy's key order; `gameVersion` is the screenshot's.
export const toScreenshotCommentMutation = (
  comment: InternalScreenshotComment | null | undefined,
  gameId: string,
  screenshot: InternalGameScreenshot,
  values: ScreenshotCommentFormValues
): ScreenshotCommentMutation => ({
  id: comment?.id ?? null,
  gameId,
  gameVersion: screenshot.gameVersion ?? null,
  published: values.published,
  analystId: values.analystId || null,
  screenshotId: screenshot.id,
  content: { comment: values.comment ?? null },
})

export const toMultipleScreenshotCommentMutation = (
  gameId: string,
  screenshotIds: string[],
  values: ScreenshotCommentFormValues
): MultipleScreenshotCommentMutation => ({
  comment: {
    id: null,
    gameId,
    gameVersion: null,
    published: values.published,
    analystId: values.analystId || null,
    screenshotId: null,
    content: { comment: values.comment ?? null },
  },
  screenshotIds,
})

const toPlainText = (html: string) =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;/g, ' ')
    .trim()

export const hasEnglishText = (comment?: ContentLanguageTranslation | null) => !!toPlainText(comment?.en ?? '')

export type ScreenshotCommentStoredField = 'gameId' | 'gameVersion' | 'screenshotId' | 'analyst' | 'published' | 'comment' | 'screenshotLink'

const getUnstoredCommentFields = (sent: ScreenshotCommentMutation, stored: InternalScreenshotComment, screenshotId: string) => {
  const unstored: ScreenshotCommentStoredField[] = []
  if (stored.gameId !== sent.gameId) unstored.push('gameId')
  if (sent.gameVersion !== null && (stored.gameVersion ?? null) !== sent.gameVersion) unstored.push('gameVersion')
  if ((stored.screenshotId ?? null) !== screenshotId) unstored.push('screenshotId')
  if ((stored.analystId ?? null) !== sent.analystId) unstored.push('analyst')
  if (!!stored.published !== sent.published) unstored.push('published')
  if (!isSameTranslation(sent.content.comment, stored.content?.comment)) unstored.push('comment')
  return unstored
}

const isLinked = (screenshot: InternalGameScreenshot, comment: InternalScreenshotComment) =>
  screenshot.commentId === comment.id && !!screenshot.commentPublished === !!comment.published

export const getUnstoredScreenshotCommentFields = (
  sent: ScreenshotCommentMutation,
  stored: InternalScreenshotComment,
  screenshot: InternalGameScreenshot | undefined
): ScreenshotCommentStoredField[] => {
  const unstored = getUnstoredCommentFields(sent, stored, sent.screenshotId ?? '')
  if (!screenshot || !isLinked(screenshot, stored)) unstored.push('screenshotLink')
  return unstored
}

// Screenshot ids whose stored comment isn't a copy of the one sent.
export const getUnstoredMultipleCommentScreenshotIds = (
  sent: MultipleScreenshotCommentMutation,
  screenshots: InternalGameScreenshot[],
  comments: Record<string, InternalScreenshotComment | null>
) =>
  sent.screenshotIds.filter((screenshotId) => {
    const screenshot = screenshots.find(({ id }) => id === screenshotId)
    const comment = screenshot?.commentId ? comments[screenshot.commentId] : null
    if (!screenshot || !comment) return true
    return getUnstoredCommentFields(sent.comment, comment, screenshotId).length > 0 || !isLinked(screenshot, comment)
  })

export const screenshotCommentFieldLabelKeys: Record<ScreenshotCommentStoredField, string> = {
  gameId: storedFieldLabelKeys.gameId,
  gameVersion: storedFieldLabelKeys.gameVersion,
  analyst: storedFieldLabelKeys.analyst,
  published: storedFieldLabelKeys.published,
  comment: storedFieldLabelKeys.comment,
  screenshotId: 'internal-analysts:implementation_comment_field_screenshot',
  screenshotLink: 'internal-analysts:implementation_comment_field_screenshot_link',
}
